// components/HeroSection.js
import React, { useState, useRef, useEffect } from 'react';
import { Box, Typography, useTheme, useMediaQuery } from '@mui/material';
import WorldVideo from "../images/world.mp4";
import MouseIcon from '@mui/icons-material/Mouse';
import { ExpandMore } from '@mui/icons-material';

const HeroSection = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [videoLoaded, setVideoLoaded] = useState(false);
  const videoRef = useRef(null);
  const galaxyCanvasRef = useRef(null);
  const galaxyAnimationRef = useRef(null);
  const gravityCanvasRef = useRef(null);
  const mouseRef = useRef({ x: -9999, y: -9999, on: false });

  const handleScrollDown = () => {
    window.scrollBy({
      top: window.innerHeight - 100,
      behavior: 'smooth'
    });
  };

  // Galaxy animation (only shows until video loads)
  useEffect(() => {
    // Don't run if video is loaded
    if (videoLoaded) return;

    const canvas = galaxyCanvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    const stars = [];
    const shootingStars = [];
    const nebulae = [];
    
    // Camera position and movement
    const camera = {
      x: 0,
      y: 0,
      z: 0,
      speed: 0.2,
      angle: 0,
      rotationSpeed: 0.0001
    };
    
    // Set canvas size
    const resizeCanvas = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    
    resizeCanvas();
    window.addEventListener('resize', resizeCanvas);

    // Create stars with depth layers
    const createStars = () => {
      const starCount = isMobile ? 200 : 400;
      
      for (let layer = 0; layer < 3; layer++) {
        const layerMultiplier = layer + 1;
        const starsInLayer = Math.floor(starCount / 3);
        
        for (let i = 0; i < starsInLayer; i++) {
          stars.push({
            x: (Math.random() - 0.5) * canvas.width * 3,
            y: (Math.random() - 0.5) * canvas.height * 3,
            z: Math.random() * 1000 + 100 * layerMultiplier,
            size: Math.random() * 2 + 0.5 * layerMultiplier,
            speed: Math.random() * 0.3 + 0.1 * layerMultiplier,
            brightness: Math.random() * 0.5 + 0.5,
            twinkleSpeed: Math.random() * 0.05 + 0.02,
            layer: layer
          });
        }
      }
    };

    // Create nebulae for background depth
    const createNebulae = () => {
      for (let i = 0; i < 3; i++) {
        nebulae.push({
          x: Math.random() * canvas.width * 2 - canvas.width,
          y: Math.random() * canvas.height * 2 - canvas.height,
          z: Math.random() * 500 + 200,
          width: Math.random() * 300 + 200,
          height: Math.random() * 300 + 200,
          color: `rgba(${Math.random() * 50 + 50}, ${Math.random() * 30 + 30}, ${Math.random() * 100 + 100}, ${Math.random() * 0.1 + 0.05})`,
          speed: Math.random() * 0.1 + 0.05
        });
      }
    };

    // Create shooting stars
    const createShootingStar = () => {
      shootingStars.push({
        x: (Math.random() - 0.5) * canvas.width,
        y: (Math.random() - 0.5) * canvas.height,
        z: Math.random() * 300 + 100,
        length: Math.random() * 80 + 50,
        speed: Math.random() * 15 + 8,
        angle: Math.random() * Math.PI / 3 + Math.PI / 6,
        life: 1,
        decay: Math.random() * 0.02 + 0.01
      });
    };

    // Convert 3D coordinates to 2D screen coordinates
    const project = (x, y, z) => {
      const scale = 1000 / (z + 1000);
      return {
        x: x * scale + canvas.width / 2,
        y: y * scale + canvas.height / 2,
        scale: scale
      };
    };

    // Draw nebulae
    const drawNebulae = () => {
      nebulae.forEach(nebula => {
        const projected = project(nebula.x - camera.x, nebula.y - camera.y, nebula.z - camera.z);
        const width = nebula.width * projected.scale;
      
        if (projected.scale > 0) {
          const gradient = ctx.createRadialGradient(
            projected.x, projected.y, 0,
            projected.x, projected.y, width
          );
          
          gradient.addColorStop(0, nebula.color);
          gradient.addColorStop(1, 'rgba(0, 0, 0, 0)');
          
          ctx.fillStyle = gradient;
          ctx.beginPath();
          ctx.arc(projected.x, projected.y, width, 0, Math.PI * 2);
          ctx.fill();
        }
        
        nebula.z -= nebula.speed;
        if (nebula.z < 100) {
          nebula.z = 1000;
          nebula.x = Math.random() * canvas.width * 2 - canvas.width;
          nebula.y = Math.random() * canvas.height * 2 - canvas.height;
        }
      });
    };

    // Draw stars with depth sorting
    const drawStars = () => {
      stars.sort((a, b) => b.z - a.z);
      
      stars.forEach(star => {
        const projected = project(star.x - camera.x, star.y - camera.y, star.z - camera.z);
        
        if (projected.scale > 0 && projected.x >= 0 && projected.x <= canvas.width && 
            projected.y >= 0 && projected.y <= canvas.height) {
          
          ctx.beginPath();
          const size = star.size * projected.scale;
          ctx.arc(projected.x, projected.y, Math.max(size, 0.5), 0, Math.PI * 2);
          
          const brightness = star.brightness + Math.sin(Date.now() * star.twinkleSpeed) * 0.3;
          const alpha = brightness * projected.scale;
          ctx.fillStyle = `rgba(255, 255, 255, ${Math.min(alpha, 1)})`;
          ctx.fill();
          
          star.z -= star.speed;
          if (star.z < 100) {
            star.z = 1000;
            star.x = (Math.random() - 0.5) * canvas.width * 3;
            star.y = (Math.random() - 0.5) * canvas.height * 3;
          }
        }
      });
    };

    // Draw shooting stars with 3D effect
    const drawShootingStars = () => {
      shootingStars.forEach((star, index) => {
        const projected = project(star.x - camera.x, star.y - camera.y, star.z - camera.z);
        
        if (projected.scale > 0) {
          ctx.save();
          ctx.translate(projected.x, projected.y);
          ctx.rotate(star.angle);
          
          const length = star.length * projected.scale;
          const gradient = ctx.createLinearGradient(0, 0, length, 0);
          gradient.addColorStop(0, 'rgba(255, 255, 255, 0.9)');
          gradient.addColorStop(0.3, 'rgba(173, 216, 230, 0.7)');
          gradient.addColorStop(0.7, 'rgba(100, 150, 255, 0.4)');
          gradient.addColorStop(1, 'rgba(255, 255, 255, 0)');
          
          ctx.strokeStyle = gradient;
          ctx.lineWidth = 2 * projected.scale;
          ctx.globalAlpha = star.life * projected.scale;
          
          ctx.beginPath();
          ctx.moveTo(0, 0);
          ctx.lineTo(length, 0);
          ctx.stroke();
          
          ctx.restore();
        }
        
        star.x += Math.cos(star.angle) * star.speed * projected.scale;
        star.y += Math.sin(star.angle) * star.speed * projected.scale;
        star.z -= star.speed * 2;
        star.life -= star.decay;
        
        if (star.life <= 0 || star.z < 50) {
          shootingStars.splice(index, 1);
        }
      });
    };

    // Update camera movement
    const updateCamera = () => {
      camera.angle += camera.rotationSpeed;
      camera.x = Math.cos(camera.angle) * 100;
      camera.y = Math.sin(camera.angle * 0.7) * 50;
      camera.z += camera.speed;
    };

    // Animation loop
    const animate = () => {
      // Stop animation if video loaded
      if (videoLoaded) return;
      
      // Clear canvas with dark space color
      ctx.fillStyle = 'rgba(5, 5, 15, 0.3)';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      
      updateCamera();
      drawNebulae();
      drawStars();
      drawShootingStars();
      
      if (Math.random() < 0.03) {
        createShootingStar();
      }
      
      galaxyAnimationRef.current = requestAnimationFrame(animate);
    };

    createStars();
    createNebulae();
    animate();

    return () => {
      window.removeEventListener('resize', resizeCanvas);
      if (galaxyAnimationRef.current) {
        cancelAnimationFrame(galaxyAnimationRef.current);
      }
    };
  }, [videoLoaded, isMobile]);

  // Gravity Particle Effect (Mouse Proximity) - Always on top
  useEffect(() => {
    if (isMobile) return;
    
    const canvas = gravityCanvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext('2d');
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    
    // Canvas sizing with DPR
    const setCanvasSize = () => {
      const w = window.innerWidth;
      const h = window.innerHeight;
      canvas.width = w * dpr;
      canvas.height = h * dpr;
      canvas.style.width = `${w}px`;
      canvas.style.height = `${h}px`;
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      ctx.scale(dpr, dpr);
      return { w, h };
    };
    
    let { w: W, h: H } = setCanvasSize();
    
    const N = 110;
    const CONNECT_D = 120;
    const ATTRACT_R = 160;
    const ATTRACT_STR = 0.012;
    const REPEL_R = 70;
    const REPEL_STR = 0.06;
    const FRICTION = 0.96;
    const DRIFT = 0.012;
    
    const GRAD = [[62,12,140],[0,180,255],[200,230,255]];
    const lerpColor = t => {
      const seg = t < 0.5 ? 0 : 1;
      const lt = t < 0.5 ? t * 2 : (t - 0.5) * 2;
      return GRAD[seg].map((v, i) => v + (GRAD[seg+1][i] - v) * lt);
    };
    
    let pts = [];
    let time = 0;
    let last = 0;
    let raf;
    const pulses = [];
    let pulseTimer = 0;
    
    const initParticles = () => {
      pts = Array.from({length: N}, () => {
        const x = Math.random() * W;
        const y = Math.random() * H;
        return { 
          x, y, ox: x, oy: y, 
          vx: (Math.random() - 0.5) * 0.6, 
          vy: (Math.random() - 0.5) * 0.6,
          r: 1.6 + Math.random() * 1.8, 
          ang: Math.random() * Math.PI * 2,
          spd: 0.004 + Math.random() * 0.008, 
          amp: 20 + Math.random() * 40,
          ct: Math.random(), 
          pp: Math.random() * Math.PI * 2 
        };
      });
    };
    
    const onMove = (e) => { 
      mouseRef.current = { x: e.clientX, y: e.clientY, on: true }; 
    };
    const onLeave = () => { 
      mouseRef.current = { x: -9999, y: -9999, on: false }; 
    };
    
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseleave', onLeave);
    
    const handleResize = () => {
      const dims = setCanvasSize();
      W = dims.w;
      H = dims.h;
      initParticles();
    };
    
    window.addEventListener('resize', handleResize);
    
    const frame = (ts) => {
      raf = requestAnimationFrame(frame);
      const dt = Math.min((ts - last) / 16.67, 3);
      last = ts;
      time += 0.016 * dt;
      pulseTimer += dt;
      
      if (pulseTimer > 18 && mouseRef.current.on) {
        pulseTimer = 0;
        pulses.push({ x: mouseRef.current.x, y: mouseRef.current.y, r: 0, a: 0.5 });
      }
      
      // Clear with transparency to show video/background below
      ctx.clearRect(0, 0, W, H);
      
      for (const p of pts) {
        p.ang += p.spd * dt;
        const tx = p.ox + Math.cos(p.ang) * p.amp;
        const ty = p.oy + Math.sin(p.ang * 0.7) * p.amp * 0.6;
        p.vx += (tx - p.x) * DRIFT * dt;
        p.vy += (ty - p.y) * DRIFT * dt;
        
        const { x: mx, y: my, on } = mouseRef.current;
        if (on) {
          const dx = p.x - mx;
          const dy = p.y - my;
          const d = Math.sqrt(dx * dx + dy * dy) || 0.001;
          if (d < REPEL_R) {
            const s = (1 - d / REPEL_R) * REPEL_STR * dt;
            p.vx += (dx / d) * s * REPEL_R * 0.1;
            p.vy += (dy / d) * s * REPEL_R * 0.1;
          } else if (d < ATTRACT_R) {
            const s = (1 - (d - REPEL_R) / (ATTRACT_R - REPEL_R)) * ATTRACT_STR * dt;
            p.vx += (-dy / d) * s * 30 - (dx / d) * s * 8;
            p.vy += (dx / d) * s * 30 - (dy / d) * s * 8;
          }
        }
        
        p.vx *= Math.pow(FRICTION, dt);
        p.vy *= Math.pow(FRICTION, dt);
        p.x += p.vx * dt;
        p.y += p.vy * dt;
        
        if (p.x < 0) { p.x = 0; p.vx *= -0.5; }
        if (p.x > W) { p.x = W; p.vx *= -0.5; }
        if (p.y < 0) { p.y = 0; p.vy *= -0.5; }
        if (p.y > H) { p.y = H; p.vy *= -0.5; }
      }
      
      // Draw connections (threads)
      for (let i = 0; i < pts.length; i++) {
        for (let j = i + 1; j < pts.length; j++) {
          const a = pts[i];
          const b = pts[j];
          const dx = b.x - a.x;
          const dy = b.y - a.y;
          const d = Math.sqrt(dx * dx + dy * dy);
          if (d > CONNECT_D) continue;
          
          const t = 1 - d / CONNECT_D;
          const [r, g, bl] = lerpColor((a.ct + b.ct) * 0.5);
          const avgSpd = (Math.sqrt(a.vx * a.vx + a.vy * a.vy) + Math.sqrt(b.vx * b.vx + b.vy * b.vy)) * 0.5;
          const boost = Math.min(avgSpd * 0.4, 0.6);
          
          ctx.save();
          ctx.strokeStyle = `rgba(${r}, ${g}, ${bl}, ${t * t * (0.18 + boost)})`;
          ctx.lineWidth = t * (0.6 + boost * 1.2);
          ctx.beginPath();
          ctx.moveTo(a.x, a.y);
          ctx.quadraticCurveTo((a.x + b.x) * 0.5, (a.y + b.y) * 0.5 + d * 0.04, b.x, b.y);
          ctx.stroke();
          ctx.restore();
        }
      }
      
      // Draw particles
      for (const p of pts) {
        const spd = Math.sqrt(p.vx * p.vx + p.vy * p.vy);
        const pulse = 0.7 + 0.3 * Math.sin(time * 2 + p.pp);
        const r = p.r * pulse + Math.min(spd * 0.6, 2);
        const [rc, gc, bc] = lerpColor(p.ct);
        const al = 0.55 + Math.min(spd * 0.2, 0.4);
        
        if (spd > 0.3) {
          const gg = ctx.createRadialGradient(p.x, p.y, 0, p.x, p.y, r * 5);
          gg.addColorStop(0, `rgba(${rc}, ${gc}, ${bc}, ${al * 0.5})`);
          gg.addColorStop(1, `rgba(${rc}, ${gc}, ${bc}, 0)`);
          ctx.beginPath();
          ctx.arc(p.x, p.y, r * 5, 0, Math.PI * 2);
          ctx.fillStyle = gg;
          ctx.fill();
        }
        
        ctx.beginPath();
        ctx.arc(p.x, p.y, r, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(${rc}, ${gc}, ${bc}, ${al})`;
        ctx.fill();
      }
      
      // Draw cursor visual and pulses
      const { x: mx, y: my, on } = mouseRef.current;
      if (on) {
        const g = ctx.createRadialGradient(mx, my, 0, mx, my, ATTRACT_R);
        g.addColorStop(0, 'rgba(0, 200, 255, 0.08)');
        g.addColorStop(1, 'rgba(0, 0, 0, 0)');
        ctx.fillStyle = g;
        ctx.beginPath();
        ctx.arc(mx, my, ATTRACT_R, 0, Math.PI * 2);
        ctx.fill();
        
        ctx.save();
        ctx.strokeStyle = 'rgba(0, 220, 255, 0.6)';
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.arc(mx, my, 12, 0, Math.PI * 2);
        ctx.stroke();
        ctx.fillStyle = 'rgba(0, 220, 255, 0.9)';
        ctx.beginPath();
        ctx.arc(mx, my, 2.5, 0, Math.PI * 2);
        ctx.fill();
        ctx.restore();
        
        for (let i = pulses.length - 1; i >= 0; i--) {
          const p = pulses[i];
          ctx.save();
          ctx.strokeStyle = `rgba(0, 200, 255, ${p.a})`;
          ctx.lineWidth = 0.8;
          ctx.beginPath();
          ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
          ctx.stroke();
          ctx.restore();
          p.r += 2.5;
          p.a -= 0.012;
          if (p.a <= 0) pulses.splice(i, 1);
        }
      }
    };
    
    initParticles();
    raf = requestAnimationFrame(frame);
    
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseleave', onLeave);
      window.removeEventListener('resize', handleResize);
    };
  }, [isMobile]);

  const handleVideoLoad = () => {
    setVideoLoaded(true);
  };

  return (
    <Box sx={{ 
      height: "100vh", 
      overflow: 'hidden',
      position: 'relative',
      fontFamily: "'Inter', 'SF Pro Display', -apple-system, BlinkMacSystemFont, sans-serif"
    }}>
      {/* Galaxy Animation Canvas - Shows until video loads */}
      <canvas
        ref={galaxyCanvasRef}
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          zIndex: -2,
          opacity: videoLoaded ? 0 : 1,
          transition: 'opacity 1.5s ease-in-out',
          pointerEvents: 'none'
        }}
      />

      {/* Video Background - Main background */}
      <video 
        ref={videoRef}
        autoPlay
        loop
        muted
        playsInline
        onLoadedData={handleVideoLoad}
        onCanPlay={handleVideoLoad}
        style={{ 
          width: '100%', 
          height: '100%', 
          objectFit: 'cover',
          filter: 'brightness(0.7)',
          position: 'fixed',
          top: 0,
          left: 0,
          zIndex: -1,
          opacity: videoLoaded ? 1 : 0,
          transition: 'opacity 1.5s ease-in-out'
        }}
      >
        <source src={WorldVideo} type="video/mp4" />
        Your browser does not support the video tag.
      </video>
      
      {/* Gravity Particle Canvas - Overlays on top */}
      {!isMobile && (
        <canvas
          ref={gravityCanvasRef}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            zIndex: -1,
            pointerEvents: 'none', // Allows clicking through
          }}
        />
      )}
      
      {/* Overlay Content */}
      <Box 
        sx={{
          position:"fixed",
          top: '40%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          textAlign: 'center',
          color: '#ffffff',
          textShadow: '2px 2px 12px rgba(0, 0, 0, 0.8)',
          width: '100%',
          zIndex: -1,
          px: 2,
          fontFamily: "'Inter', sans-serif",
          pointerEvents: 'none' // Allows mouse events to pass through
        }}
      >
        <Typography variant="h2" component="h1" gutterBottom sx={{ 
          fontSize: isMobile ? '2.5rem' : '4.5rem',
          fontWeight: 800,
          lineHeight: 1.1,
          px: isMobile ? 2 : 0,
          letterSpacing: '-0.02em',
          fontFamily: "'Inter', 'SF Pro Display', -apple-system, sans-serif",
          color: '#ffffff',
          mb: 2
        }}>
          AediaX
        </Typography>
        
        <Typography variant="h3" component="h2" sx={{ 
          fontSize: isMobile ? '1.4rem' : '2.2rem',
          fontWeight: 400,
          lineHeight: 1.3,
          px: isMobile ? 2 : 0,
          letterSpacing: '0.01em',
          fontFamily: "'Inter', -apple-system, sans-serif",
          color: '#ffffff',
          maxWidth: '800px',
          margin: '0 auto'
        }}>
          Your Idea, Our AI-Powered Software
        </Typography>
      </Box>

      {/* Scroll Down Indicator */}
      <Box
        onClick={handleScrollDown}
        sx={{
          position: 'absolute',
          bottom: '30px',
          left: '50%',
          transform: 'translateX(-50%)',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          cursor: 'pointer',
          color: 'white',
          zIndex: 3,
          transition: 'opacity 0.5s ease-in-out'
        }}
      >
        <Box sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          animation: 'bounce 2s infinite',
          '@keyframes bounce': {
            '0%, 20%, 50%, 80%, 100%': { transform: 'translateY(0)' },
            '40%': { transform: 'translateY(-10px)' },
            '60%': { transform: 'translateY(-5px)' }
          }
        }}>
          <MouseIcon sx={{ 
            fontSize: isMobile ? '2rem' : '2.5rem',
            marginBottom: '-8px'
          }} />
          <ExpandMore sx={{ 
            fontSize: isMobile ? '2rem' : '2.5rem',
            animation: 'pulse 1.5s infinite',
            '@keyframes pulse': {
              '0%, 100%': { opacity: 0.8 },
              '50%': { opacity: 0.3 }
            }
          }} />
        </Box>
      </Box>
    </Box>
  );
};

export default HeroSection;